import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import {
    computed,
    inject,
    type InjectionKey,
    provide,
    ref,
    type Ref
} from 'vue'
import type { WaitlistStatus } from '~/resources/graphql/graphql'

export enum SavedParticipantEligibility {
    CLOSED,
    NOT_ON_WAITLIST,
    WAITLISTED,
    ELIGIBLE
}

export const WaitlistParticipantConstraintPlanContractFragment = graphql(`
    fragment WaitlistParticipantConstraintPlanContractFragment on PlanContract {
        course {
            bookingStatus
            savedParticipantsWithWaitlistStatus {
                customer {
                    id
                }
                status
            }
        }
    }
`)

const waitlistParticipantEligibilityKey: InjectionKey<{
    getSavedParticipantEligibility: (
        customerId: string
    ) => SavedParticipantEligibility
    savedParticipantsAddedByBookingId: Ref<Map<string, WaitlistStatus | null>>
}> = Symbol('WaitlistParticipantEligibility')

export function useWaitlistParticipantEligibility() {
    const result = inject(waitlistParticipantEligibilityKey)

    if (result === undefined) {
        throw new Error()
    }

    return result
}

export function useWaitlistParticipantEligibilityOptional() {
    return inject(waitlistParticipantEligibilityKey, null)
}

export function createWaitlistParticipantEligibility(
    contract: Ref<
        FragmentType<typeof WaitlistParticipantConstraintPlanContractFragment>
    >
) {
    const contractData = computed(() =>
        getFragmentData(
            WaitlistParticipantConstraintPlanContractFragment,
            contract.value
        )
    )

    const savedParticipantsAddedByBookingId = ref<Map<string, WaitlistStatus>>(
        new Map()
    )

    function getSavedParticipantEligibility(customerId: string) {
        // No course means is not possible anymore
        if (contractData.value.course === null) {
            throw new Error()
        }

        // No limits on booking
        if (contractData.value.course.bookingStatus === 'BOOKING') {
            return SavedParticipantEligibility.ELIGIBLE
        }

        // Booking closed
        if (contractData.value.course.bookingStatus === 'CLOSED') {
            return SavedParticipantEligibility.CLOSED
        }

        // Booking waitlist
        if (contractData.value.course.bookingStatus === 'WAITLIST') {
            let result: WaitlistStatus | null | undefined =
                contractData.value.course.savedParticipantsWithWaitlistStatus.find(
                    participant => participant.customer.id === customerId
                )?.status

            // Check if we added this person via booking id
            if (result === undefined) {
                result = savedParticipantsAddedByBookingId.value.get(customerId)
            }

            // No saved participant found
            if (result === undefined) {
                throw new Error()
            }

            if (result === null) {
                return SavedParticipantEligibility.NOT_ON_WAITLIST
            }

            if (result === 'WAITLISTED') {
                return SavedParticipantEligibility.WAITLISTED
            }

            if (result === 'BOOKING') {
                return SavedParticipantEligibility.ELIGIBLE
            }
        }

        // Unknown status
        throw new Error()
    }

    provide(waitlistParticipantEligibilityKey, {
        getSavedParticipantEligibility,
        savedParticipantsAddedByBookingId
    })
}
